// ** Type import
import type { HorizontalNavItemsType } from '@erikdakoda/site-template-ui/types';
import type { VerticalNavItemsType } from '@erikdakoda/site-template-ui/types';
import {
  getDefaultSyncDatabaseSourceLabel,
  getSyncDatabaseMenuTitle,
  isLocalDevelopmentEnvironment,
  neonDbEnvVarToLabel,
  type DbSyncEnvParams,
} from '@erikdakoda/database/shared/neonDbEnvConfig';

const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV;
const defaultSyncSource = getDefaultSyncDatabaseSourceLabel(vercelEnv);

/** Confirms and sends the admin sync-database Inngest event. */
function confirmSyncDatabase(params: DbSyncEnvParams = {}): void {
  const source = params.remoteEnv ? neonDbEnvVarToLabel(params.remoteEnv) : defaultSyncSource;
  if (window.confirm(`Sync DB from ${source}? This will overwrite existing configurations.`)) {
    void fetch('/api/send-inngest-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'admin/sync-database', data: params }),
    });
  }
}

const syncDatabaseNavItems: HorizontalNavItemsType = [
  {
    title: getSyncDatabaseMenuTitle(defaultSyncSource),
    icon: 'light-database-rotate',
    roles: ['ADMIN', 'TEAM'],
    onClick: () => confirmSyncDatabase(),
  },
  ...(isLocalDevelopmentEnvironment(vercelEnv)
    ? [
        {
          title: getSyncDatabaseMenuTitle('Prev'),
          icon: 'light-database-rotate',
          roles: ['ADMIN', 'TEAM'],
          onClick: () => confirmSyncDatabase({ remoteEnv: 'NEON_URL_PREV', fromMaster: false }),
        },
      ]
    : []),
];

const navItems: HorizontalNavItemsType = [
  /* {
    title: 'Dashboard',
    path: '/dashboard',
    icon: 'gauge',
    roles: ['USER','ADMIN','TEAM'],
    disabled: true,
  }, */
  {
    title: 'Coaching',
    path: '/coaching',
    icon: 'whistle',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  /* {
    title: 'Goals',
    path: '/goals',
    icon: 'bullseye',
    roles: ['USER', 'ADMIN', 'TEAM'],
  }, */
  {
    title: 'Habits',
    path: '/habits',
    icon: 'calendar-check',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  {
    title: 'Metrics',
    path: '/metrics',
    icon: 'weight-scale',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  {
    title: 'Programs',
    path: '/programs',
    icon: 'graduation-cap',
    roles: ['USER', 'ADMIN', 'TEAM'],
  },
  {
    title: 'Admin',
    icon: 'screwdriver-wrench',
    roles: ['ADMIN', 'TEAM'],
    children: [
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Admin',
      },
      {
        title: 'Executions',
        path: '/admin/executions',
        icon: 'conveyor-belt-boxes',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Repo Packages',
        path: '/admin/repo-packages',
        icon: 'cubes',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Admin Actions',
        path: '/admin/admin-actions',
        icon: 'server',
        roles: ['ADMIN', 'TEAM'],
      },
      ...syncDatabaseNavItems,
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'AI',
      },
      {
        title: 'Bot Craft',
        path: '/admin/bot-craft',
        icon: 'robot',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Bots',
        path: '/admin/bots',
        icon: 'robot',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Prompts',
        path: '/admin/prompts',
        icon: 'square-terminal',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Compare Bots',
        path: '/admin/compare-bots',
        icon: 'light-robot-sync',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Compare Bots with Prev',
        path: '/admin/compare-bots-with-prev',
        icon: 'light-robot-sync',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'AI Models',
        path: '/admin/ai-models',
        icon: 'hexagon-nodes',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Model Scores',
        path: '/admin/model-scores',
        icon: 'chart-simple',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'AI Tools',
        path: '/admin/ai-tools',
        icon: 'hammer',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'MCP Servers',
        path: '/admin/mcp-servers',
        icon: 'light-mcp',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Tool Sets',
        path: '/admin/tool-sets',
        icon: 'toolbox',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Agent Convos',
        path: '/admin/agent-convos',
        icon: 'message-lines',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Analytics',
      },
      {
        title: 'Event Definitions',
        path: '/admin/event-definitions',
        icon: 'timeline',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Users',
      },
      {
        title: 'Users',
        path: '/admin/users',
        icon: 'users',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Convos',
        path: '/admin/convos',
        icon: 'message-lines',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Factoids',
        path: '/admin/factoids',
        icon: 'head-side-brain',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Todos',
        path: '/admin/todo',
        icon: 'clipboard-check',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Notifications',
        path: '/admin/notifications',
        icon: 'bell',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Feedback',
        path: '/admin/feedback',
        icon: 'bullhorn',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Ratings',
        path: '/admin/ratings',
        icon: 'thumbs-up',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Settings',
        path: '/admin/settings',
        icon: 'square-sliders',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Setting Templates',
        path: '/admin/setting-templates',
        icon: 'square-sliders',
        roles: ['ADMIN'],
      },
      {
        title: 'Connections',
        path: '/admin/connections',
        icon: 'puzzle-piece',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Programs',
      },
      {
        title: 'Programs',
        path: '/admin/programs',
        icon: 'graduation-cap',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Program Phases',
        path: '/admin/program-phases',
        icon: 'diagram-next',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Program Lessons',
        path: '/admin/program-lessons',
        icon: 'book-spine',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Program Steps',
        path: '/admin/program-steps',
        icon: 'list-ol',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Program Habits',
        path: '/admin/program-habits',
        icon: 'calendar-check',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Program Metrics',
        path: '/admin/program-metrics',
        icon: 'weight-scale',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Program Parameters',
        path: '/admin/program-parameters',
        icon: 'sliders',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'User Programs',
        path: '/admin/user-programs',
        icon: 'user',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Metrics',
      },
      {
        title: 'Units',
        path: '/admin/units',
        icon: 'weight-hanging',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Metrics',
        path: '/admin/metrics',
        icon: 'weight-scale',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Metric Groups',
        path: '/admin/metric-groups',
        icon: 'layer-group',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'User Metrics',
        path: '/admin/user-metrics',
        icon: 'user',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Metric Values',
        path: '/admin/metric-values',
        icon: 'ruler',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Metric Sync Audits',
        path: '/admin/metric-sync-audits',
        icon: 'clipboard-check',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Quizzes',
      },
      {
        title: 'Quizzes',
        path: '/admin/quizzes',
        icon: 'light-clipboard-quiz',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Quiz Results',
        path: '/admin/quiz-results',
        icon: 'light-clipboard-quiz',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Quiz Summary',
        path: '/admin/quiz-summary',
        icon: 'light-clipboard-quiz',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Taxonomy',
      },
      {
        title: 'Life Domains',
        path: '/admin/life-domains',
        icon: 'dharmachakra',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Taxonomy',
        path: '/admin/taxonomy',
        icon: 'compass',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Taxonomy Search Logs',
        path: '/admin/taxonomy-search-logs',
        icon: 'light-compass-circle-bolt',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Goodreads List',
        path: '/admin/goodreads-list',
        icon: 'goodreads-g',
        prefix: 'fab',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Resources',
        path: '/admin/resources',
        icon: 'photo-film-music',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Resource Links',
        path: '/admin/resource-links',
        icon: 'light-photo-film-music-globe',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Books',
        path: '/admin/books',
        icon: 'book-spine',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Podcasts',
        path: '/admin/podcasts',
        icon: 'podcast',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Pod Episodes',
        path: '/admin/pod-episodes',
        icon: 'microphone-lines',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Search YouTube',
        path: '/admin/search-youtube',
        icon: 'light-youtube-search',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Channels',
        path: '/admin/channels',
        icon: 'light-youtube',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Videos',
        path: '/admin/videos',
        icon: 'camera-movie',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Authors',
        path: '/admin/authors',
        icon: 'pen-nib',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        roles: ['ADMIN', 'TEAM'],
        sectionTitle: 'Test pages',
      },
      {
        title: 'Theme',
        path: '/theme',
        icon: 'palette',
        roles: ['ADMIN', 'TEAM'],
      },
      {
        title: 'Markdown Editor',
        path: '/admin/markdown-editor',
        icon: 'light-markdown-book-pen',
        roles: ['USER', 'ADMIN'],
      },
      {
        title: 'AI Test Chat',
        path: '/admin/ai-test-chat',
        icon: 'message-lines',
        roles: ['ADMIN', 'TEAM'],
      },
    ],
  },
];

export const HorizontalNavItems = () => navItems as HorizontalNavItemsType;

export const VerticalNavItems = () => navItems as VerticalNavItemsType;
